import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@/components/ui/accordion';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Loader2, FileText, Download, X, ArrowLeft, ClipboardList, Wine,
  BarChart3, Scale, Layers, Printer, Eye, Edit3, Plus,
  FileSpreadsheet, Truck, Undo2, ArrowLeftRight, Trash2, TrendingUp, Receipt, Send,
  History, ChevronDown, ChevronUp, RefreshCw, Link2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import SendReportPackDialog from './SendReportPackDialog';
import ReportReconciliationDialog from './ReportReconciliationDialog';
import api from '../services/api';
import { reportsRepositoryService, stocktakeSessionsService } from '../services';

/**
 * Report Centre — single session-aware Stocktake Reports surface.
 *
 * Organised into the five report families (Summary, Core Stock Audit,
 * Valuation, Supporting Schedules, Full Report Pack). Retains the
 * existing working report endpoints unchanged (Stock On Hand,
 * Estimated vs Till, Stock Audit Report, Executive Summary, Summary
 * Stock Audit Report wizard) — no calculation logic is re-derived
 * here, this is purely a navigation/download surface over the
 * existing manifests and services.
 *
 * Same props/contract as the previous flat dialog so all call sites
 * (wizard header, top-level Reports page, multi-location drilldown)
 * keep working unchanged.
 */
/**
 * Two small buttons — PDF (solid) + Excel (outline) — for any report
 * offering both export formats. `baseKey` matches the catalog key so
 * applicability/disabled logic stays centralised in the parent; the
 * busy state is suffixed per-format so only the clicked button spins.
 */
function ReportFormatButtons({ baseKey, busyKey, disabled, onPdf, onXlsx, testIdBase }) {
  return (
    <div className="flex gap-1.5 shrink-0">
      <Button
        size="sm"
        disabled={busyKey === `${baseKey}_pdf` || disabled}
        onClick={onPdf}
        data-testid={`${testIdBase}-pdf-btn`}
      >
        {busyKey === `${baseKey}_pdf` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}
        PDF
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={busyKey === `${baseKey}_xlsx` || disabled}
        onClick={onXlsx}
        data-testid={`${testIdBase}-xlsx-btn`}
      >
        {busyKey === `${baseKey}_xlsx` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileSpreadsheet className="h-3 w-3 mr-1" />}
        Excel
      </Button>
    </div>
  );
}

/**
 * Batch "Download All" trigger — reused both globally (whole session)
 * and per-family (2026-09 feature). Jobs run sequentially, staggered,
 * so the browser doesn't choke on many simultaneous blob downloads;
 * each individual handler already has its own error toast, so a
 * failure in one report doesn't stop the rest of the batch.
 */
function DownloadAllButton({ label, batchKey, batchRunning, jobCount, onRun, testId }) {
  if (jobCount === 0) return null;
  const running = batchRunning === batchKey;
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={!!batchRunning}
      onClick={onRun}
      data-testid={testId}
    >
      {running ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1.5" />}
      {running ? `${label}…` : `${label} (${jobCount})`}
    </Button>
  );
}

/**
 * Full Report Pack tile control (Phase 5, 2026-09) — async
 * generate/poll/download, replacing the old one-click blocking
 * download. Idle -> Generating… -> Ready (Download) / Failed (Retry).
 * `label` optional override (used for single-report async jobs like
 * Stock Audit Analytics — iter588 — which reuse the same pipeline).
 */
function PackGenerateButton({ packType, job, onGenerate, onDownload, onRegenerate, downloading, label: labelOverride, variant: variantOverride }) {
  const status = job?.status;
  const label = labelOverride ?? (packType === 'pdf' ? 'PDF' : 'Excel');
  const variant = variantOverride ?? (packType === 'pdf' ? 'default' : 'outline');
  const icon = variantOverride === 'outline'
    ? <FileSpreadsheet className="h-3 w-3 mr-1" />
    : <FileText className="h-3 w-3 mr-1" />;
  if (status === 'generating') {
    const progressText = job?.progress_label || 'Generating…';
    return (
      <Button size="sm" disabled data-testid={`report-pack-generate-${packType}-btn`}>
        <Loader2 className="h-3 w-3 mr-1 animate-spin" />{progressText}
      </Button>
    );
  }
  if (status === 'ready') {
    if (onRegenerate) {
      return (
        <div className="flex gap-1">
          <Button
            size="sm"
            disabled={downloading}
            onClick={() => onDownload(job.version_id, job.filename)}
            data-testid={`report-pack-generate-${packType}-btn`}
          >
            {downloading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
            Download
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={downloading}
            onClick={onRegenerate}
            data-testid={`report-pack-regenerate-${packType}-btn`}
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Regenerate
          </Button>
        </div>
      );
    }
    return (
      <Button
        size="sm"
        disabled={downloading}
        onClick={() => onDownload(job.version_id, job.filename)}
        data-testid={`report-pack-generate-${packType}-btn`}
      >
        {downloading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
        Ready — Download
      </Button>
    );
  }
  if (status === 'failed') {
    return (
      <Button size="sm" variant="destructive" onClick={onGenerate} data-testid={`report-pack-generate-${packType}-btn`}>
        Failed — Retry
      </Button>
    );
  }
  return (
    <Button size="sm" variant={variant} onClick={onGenerate} data-testid={`report-pack-generate-${packType}-btn`}>
      {packType === 'pdf' || labelOverride ? icon : <FileSpreadsheet className="h-3 w-3 mr-1" />}
      {label}
    </Button>
  );
}

export default function StocktakeReportsDialog({ open, onOpenChange, sessionId, onBack = null }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isAdmin, isScheduler, isAuditor } = useAuth();
  const isInternalUser = isAdmin || isScheduler || isAuditor;
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [batchRunning, setBatchRunning] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [futureManifest, setFutureManifest] = useState(null);
  const [valuation, setValuation] = useState('opening');
  const [selectedSubcats, setSelectedSubcats] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [packInfo, setPackInfo] = useState(null);
  const [sendPackOpen, setSendPackOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [downloadingVersionId, setDownloadingVersionId] = useState(null);
  const [reconciliationLink, setReconciliationLink] = useState(null);
  const [launchingSummary, setLaunchingSummary] = useState(false);
  const [nearestAckDialog, setNearestAckDialog] = useState(null); // { candidate, date_diff_days } | null
  const [reconciliationOpen, setReconciliationOpen] = useState(false);
  // Phase 5 (2026-09) — async Full Report Pack generation. Each entry:
  // null (idle) | { status: 'generating'|'ready'|'failed', job_id, version_id, filename, failed_component, error_message }
  // 2026-10 iter588 — Stock Audit Analytics moved onto the SAME async
  // job pipeline to sidestep Cloudflare's 60s edge timeout on the
  // previous direct sync GET (users routinely saw axios "timeout of
  // 60000ms" or a Cloudflare 5xx parse error on large sessions).
  const [packJobs, setPackJobs] = useState({ pdf: null, xlsx: null, stock_audit_analytics: null });
  const pollTimersRef = useRef({ pdf: null, xlsx: null, stock_audit_analytics: null });
  const pollFailuresRef = useRef({ pdf: 0, xlsx: 0, stock_audit_analytics: 0 });

  const clearPackPoll = (packType) => {
    if (pollTimersRef.current[packType]) {
      clearInterval(pollTimersRef.current[packType]);
      pollTimersRef.current[packType] = null;
    }
    pollFailuresRef.current[packType] = 0;
  };

  const pollPackJob = (packType, jobId) => {
    clearPackPoll(packType);
    // iter588 — transient poll failures (Cloudflare 5xx, network
    // blip, backend cold-start) no longer instantly fail the job UI.
    // We tolerate up to 8 consecutive poll errors (~20s) before
    // surfacing "failed"; the background pack build itself is still
    // running server-side and the job status DB row is the source of
    // truth — a later poll typically recovers to "ready".
    const MAX_TRANSIENT_POLL_FAILURES = 8;
    const packLabel = packType === 'pdf'
      ? 'Full Report Pack'
      : packType === 'xlsx'
        ? 'Excel Schedules Pack'
        : 'Stock Audit Analytics';
    pollTimersRef.current[packType] = setInterval(async () => {
      try {
        const res = await api.get(`/stocktake/sessions/${sessionId}/reports/packs/jobs/${jobId}`);
        pollFailuresRef.current[packType] = 0;
        if (res.data.status !== 'generating') {
          clearPackPoll(packType);
          setPackJobs(prev => ({ ...prev, [packType]: res.data }));
          if (res.data.status === 'ready') {
            refreshPackInfo();
            toast({ title: 'Report ready', description: `${packLabel} generated.` });
          } else {
            toast({ title: `${packLabel} failed`, description: res.data.error_message || 'Unknown error', variant: 'destructive' });
          }
        }
      } catch (err) {
        pollFailuresRef.current[packType] = (pollFailuresRef.current[packType] || 0) + 1;
        if (pollFailuresRef.current[packType] >= MAX_TRANSIENT_POLL_FAILURES) {
          clearPackPoll(packType);
          const detail = err?.response?.data?.detail
            || (err?.response?.status ? `Status check unavailable (HTTP ${err.response.status}). The report may still be generating — try refreshing this dialog in a moment.` : err.message);
          setPackJobs(prev => ({ ...prev, [packType]: { status: 'failed', error_message: detail } }));
        }
        // Otherwise: keep polling — the server-side background task
        // is likely still working and the next poll should recover.
      }
    }, 2500);
  };

  const startPackGeneration = async (packType, force = false) => {
    try {
      const url = `/stocktake/sessions/${sessionId}/reports/packs/generate?pack_type=${packType}${force ? '&force=true' : ''}`;
      const res = await api.post(url);
      setPackJobs(prev => ({ ...prev, [packType]: { status: 'generating', job_id: res.data.job_id } }));
      pollPackJob(packType, res.data.job_id);
    } catch (err) {
      toast({
        title: 'Could not start pack generation',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    }
  };

  const refreshPackInfo = (data) => {
    // Pre-deployment defect sweep #6 — if the caller already has a
    // fresh version list (from a Generate/Send response), apply it
    // directly instead of firing a second GET (avoids any race where
    // the caption would only pick up the new version on next reopen).
    if (data && (data.pdf_pack_versions || data.xlsx_pack_versions)) {
      setPackInfo(prev => ({
        ...prev,
        pdf_pack_versions: data.pdf_pack_versions ?? prev?.pdf_pack_versions,
        xlsx_pack_versions: data.xlsx_pack_versions ?? prev?.xlsx_pack_versions,
      }));
      return;
    }
    if (!sessionId) return;
    api.get(`/stocktake/sessions/${sessionId}/report-packs`).then(res => setPackInfo(res.data)).catch(() => null);
  };

  const downloadPackVersion = async (versionId, filename) => {
    setDownloadingVersionId(versionId);
    try {
      await reportsRepositoryService.downloadVersion(versionId, filename);
    } catch (err) {
      toast({ title: 'Download failed', description: err.message, variant: 'destructive' });
    } finally {
      setDownloadingVersionId(null);
    }
  };

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    setManifest(null);
    setFutureManifest(null);
    setPackInfo(null);
    setReconciliationLink(null);
    Promise.all([
      api.get(`/stocktake/sessions/${sessionId}/reports/manifest`),
      api.get(`/stocktake/sessions/${sessionId}/reports/v2/manifest`).catch(() => null),
      api.get(`/stocktake/sessions/${sessionId}/report-packs`).catch(() => null),
      api.get(`/stocktake/sessions/${sessionId}/reports/packs/jobs/latest?pack_type=pdf`).catch(() => null),
      api.get(`/stocktake/sessions/${sessionId}/reports/packs/jobs/latest?pack_type=xlsx`).catch(() => null),
      api.get(`/stocktake/sessions/${sessionId}/reports/packs/jobs/latest?pack_type=stock_audit_analytics`).catch(() => null),
    ])
      .then(([res, futureRes, packRes, pdfJobRes, xlsxJobRes, saaJobRes]) => {
        setManifest(res.data);
        const firstVal = res.data?.valuations?.[0]?.key;
        if (firstVal) setValuation(firstVal);
        if (futureRes) setFutureManifest(futureRes.data);
        if (packRes) setPackInfo(packRes.data);
        // Resume polling if a pack was already generating when the
        // dialog was last closed, or show the last Ready/Failed result.
        [['pdf', pdfJobRes], ['xlsx', xlsxJobRes], ['stock_audit_analytics', saaJobRes]].forEach(([packType, jobRes]) => {
          if (jobRes?.data?.job_id) {
            setPackJobs(prev => ({ ...prev, [packType]: jobRes.data }));
            if (jobRes.data.status === 'generating') pollPackJob(packType, jobRes.data.job_id);
          }
        });
      })
      .catch(err => {
        toast({
          title: 'Could not load report manifest',
          description: err?.response?.data?.detail || err.message,
          variant: 'destructive',
        });
      })
      .finally(() => setLoading(false));
    return () => {
      clearPackPoll('pdf');
      clearPackPoll('xlsx');
      clearPackPoll('stock_audit_analytics');
    };
  }, [open, sessionId, toast]);

  // Reconciliation data is lazy-loaded only when its accordion section
  // is opened — see the "reconciliation" AccordionItem below.
  const loadReconciliationLink = () => {
    if (!isInternalUser || !sessionId || reconciliationLink) return;
    api.get(`/stocktake/sessions/${sessionId}/reconciliation`)
      .then(res => setReconciliationLink(res.data.link))
      .catch(() => null);
  };

  const toggle = (list, item, setter) => {
    setter(list.includes(item) ? list.filter(x => x !== item) : [...list, item]);
  };

  // Report Centre filter correction — the sub-category dropdown is a
  // convenience for granular, product-level reports only. Whole-audit
  // reports (Certificate of Valuation, Core Stock Audit Report, Core
  // Audit History & Reconciliation, etc.) must always represent the
  // entire session, so `key` gates whether `subcategories` is sent at
  // all; `report_applicability.py`'s `filterable` flag is the single
  // source of truth for which reports this applies to.
  const isFilterable = (key) => (manifest?.report_catalog || []).find(e => e.key === key)?.filterable === true;

  const sharedParams = (key) => {
    const params = new URLSearchParams();
    if (isFilterable(key) && selectedSubcats.length) params.set('subcategories', selectedSubcats.join(','));
    if (selectedLocations.length) params.set('location_ids', selectedLocations.join(','));
    return params;
  };

  const downloadFile = async (busyKeySuffixed, path, fileBase, ext, mime) => {
    setBusyKey(busyKeySuffixed);
    try {
      const response = await api.get(path, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileBase}_${manifest?.session?.reference || sessionId}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast({ title: 'Report ready', description: `${fileBase}.${ext} downloaded.` });
    } catch (err) {
      toast({
        title: 'Report generation failed',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusyKey(null);
    }
  };

  const downloadPdf = (key, path, fileBase) => downloadFile(`${key}_pdf`, path, fileBase, 'pdf', 'application/pdf');
  const downloadXlsx = (key, path, fileBase) => downloadFile(`${key}_xlsx`, path, fileBase, 'xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

  const handleStockOnHand = () => {
    const params = sharedParams('stock_on_hand');
    params.set('valuation', valuation);
    downloadPdf('stock_on_hand', `/stocktake/sessions/${sessionId}/reports/stock-on-hand?${params.toString()}`, `StockOnHand_${valuation}`);
  };

  const handleEstimatedVsTill = () => {
    downloadPdf('estimated_vs_till', `/stocktake/sessions/${sessionId}/reports/estimated-vs-till?${sharedParams('estimated_vs_till').toString()}`, 'EstimatedVsTill');
  };

  const handleStockAuditReport = () => {
    downloadPdf('stock_audit_report', `/stocktake/sessions/${sessionId}/reports/stock-audit-report?${sharedParams('stock_audit_report').toString()}`, 'CoreStockAuditReport');
  };

  const handleAuditHistoryReconciliation = () => {
    downloadPdf('core_audit_history_reconciliation', `/stocktake/sessions/${sessionId}/reports/core-audit-history-reconciliation?${sharedParams('core_audit_history_reconciliation').toString()}`, 'CoreAuditHistoryReconciliation');
  };

  // iter588 — Stock Audit Analytics PDF now flows through the SAME
  // async pack-job pipeline as the Full Report Pack (Cloudflare's
  // 60s edge timeout was killing the previous direct sync download
  // on large sessions). Excel export stays synchronous — it's fast.
  const handleStockAuditAnalytics = () => startPackGeneration('stock_audit_analytics');
  const handleStockAuditAnalyticsXlsx = () => {
    downloadXlsx('stock_audit_analytics', `/stocktake/sessions/${sessionId}/reports/stock-audit-analytics/xlsx`, 'StockAuditAnalytics');
  };

  const handleCertificateOfValuation = () => {
    const qs = sharedParams('valuation_report').toString();
    downloadPdf('valuation_report', `/stocktake/sessions/${sessionId}/reports/certificate-of-valuation${qs ? `?${qs}` : ''}`, 'CertificateOfValuation');
  };

  const handleDetailedStockValuation = () => {
    downloadPdf('detailed_stock_valuation', `/stocktake/sessions/${sessionId}/reports/detailed-stock-valuation?${sharedParams('detailed_stock_valuation').toString()}`, 'DetailedStockValuation');
  };

  const handleCombinedValuation = () => {
    downloadPdf('combined_valuation', `/stocktake/sessions/${sessionId}/reports/combined-valuation`, 'CertificateAndDetailedValuation');
  };

  const handleStockOnHandXlsx = () => {
    const params = sharedParams('stock_on_hand');
    params.set('valuation', valuation);
    downloadXlsx('stock_on_hand', `/stocktake/sessions/${sessionId}/reports/stock-on-hand/xlsx?${params.toString()}`, `StockOnHand_${valuation}`);
  };

  const handleEstimatedVsTillXlsx = () => {
    downloadXlsx('estimated_vs_till', `/stocktake/sessions/${sessionId}/reports/estimated-vs-till/xlsx?${sharedParams('estimated_vs_till').toString()}`, 'EstimatedVsTill');
  };

  const handleAuditHistoryReconciliationXlsx = () => {
    downloadXlsx('core_audit_history_reconciliation', `/stocktake/sessions/${sessionId}/reports/core-audit-history-reconciliation/xlsx`, 'CoreAuditHistoryReconciliation');
  };

  const handleDetailedStockValuationXlsx = () => {
    downloadXlsx('detailed_stock_valuation', `/stocktake/sessions/${sessionId}/reports/detailed-stock-valuation/xlsx?${sharedParams('detailed_stock_valuation').toString()}`, 'DetailedStockValuation');
  };

  const handleDeliveriesRegister = () => downloadPdf('deliveries_register', `/stocktake/sessions/${sessionId}/reports/deliveries-register`, 'DeliveriesRegister');
  const handleDeliveriesRegisterXlsx = () => downloadXlsx('deliveries_register', `/stocktake/sessions/${sessionId}/reports/deliveries-register/xlsx`, 'DeliveriesRegister');
  const handleCreditsRegister = () => downloadPdf('credits_register', `/stocktake/sessions/${sessionId}/reports/credits-register`, 'CreditsRegister');
  const handleCreditsRegisterXlsx = () => downloadXlsx('credits_register', `/stocktake/sessions/${sessionId}/reports/credits-register/xlsx`, 'CreditsRegister');
  const handleTransfersRegister = () => downloadPdf('transfers_register', `/stocktake/sessions/${sessionId}/reports/transfers-register`, 'TransfersRegister');
  const handleTransfersRegisterXlsx = () => downloadXlsx('transfers_register', `/stocktake/sessions/${sessionId}/reports/transfers-register/xlsx`, 'TransfersRegister');
  const handleWastageAdjustmentsDetail = () => downloadPdf('wastage_allowances_detail', `/stocktake/sessions/${sessionId}/reports/wastage-adjustments-detail`, 'WastageAdjustmentsDetail');
  const handleWastageAdjustmentsDetailXlsx = () => downloadXlsx('wastage_allowances_detail', `/stocktake/sessions/${sessionId}/reports/wastage-adjustments-detail/xlsx`, 'WastageAdjustmentsDetail');
  const handleCostMovement = () => downloadPdf('cost_movement', `/stocktake/sessions/${sessionId}/reports/cost-movement?${sharedParams('cost_movement').toString()}`, 'CostMovement');
  const handleCostMovementXlsx = () => downloadXlsx('cost_movement', `/stocktake/sessions/${sessionId}/reports/cost-movement/xlsx?${sharedParams('cost_movement').toString()}`, 'CostMovement');
  const handleSalesRegister = () => downloadPdf('sales_register', `/stocktake/sessions/${sessionId}/reports/sales-register?${sharedParams('sales_register').toString()}`, 'SalesRegister');
  const handleSalesRegisterXlsx = () => downloadXlsx('sales_register', `/stocktake/sessions/${sessionId}/reports/sales-register/xlsx?${sharedParams('sales_register').toString()}`, 'SalesRegister');

  const handleExecutiveSummary = async () => {
    setBusyKey('executive_summary');
    try {
      const response = await api.get(`/stocktake/sessions/${sessionId}/reports/v2/executive-summary`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `ExecutiveSummary_${manifest?.session?.reference || sessionId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast({ title: 'Executive Summary opened' });
    } catch (err) {
      toast({
        title: 'Executive Summary failed',
        description: err?.response?.data?.detail || err.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setBusyKey(null);
    }
  };

  const goToSummaryReport = (jobId, scope, reportId) => {
    onOpenChange(false);
    navigate(reportId ? `/summary-reports/${reportId}` : `/jobs/${jobId}/summary-report?scope=${scope}`);
  };

  // P3 — Scope B guarded launch. Replaces the blind "Create Summary
  // Report" action for LOCKED sessions: builds a retry-safe linked
  // Summary prefilled from this session's exact saved calculation.
  // Gated 503 (clean message, no write possible) until the CTO approves
  // and applies the Scope B migration — see
  // STOCKTAKE_SUMMARY_P2_CTO_PACKET.md.
  const refreshSummaryLink = async () => {
    try {
      const res = await api.get(`/stocktake/sessions/${sessionId}/reports/manifest`);
      setManifest(res.data);
    } catch (_e) { /* non-fatal — keep showing the last known state */ }
  };

  const handleLaunchSummary = async (acknowledgeNearest = false) => {
    setLaunchingSummary(true);
    try {
      const result = await stocktakeSessionsService.launchSummary(sessionId, acknowledgeNearest);
      setNearestAckDialog(null);
      toast({
        title: result.created ? 'Linked Summary launched' : 'Already linked',
        description: result.created
          ? 'A Summary pre-filled from this Stocktake has been created.'
          : 'This session is already linked to a Summary — opening it.',
      });
      onOpenChange(false);
      navigate(`/summary-reports/${result.report.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.reason === 'nearest_date_requires_ack') {
        setNearestAckDialog(detail);
        return;
      }
      if (status === 503 && detail?.reason === 'schema_pending') {
        toast({
          title: 'Pending schema approval',
          description: 'Linked-Summary launch is awaiting CTO approval of the Scope B migration. No changes were made.',
          variant: 'destructive',
        });
      } else if (status === 503) {
        toast({
          title: 'Temporarily unavailable',
          description: 'The server is briefly busy — please try again in a moment.',
          variant: 'destructive',
        });
      } else if (status === 409) {
        toast({
          title: 'Could not launch',
          description: detail?.message || 'A linked Summary already exists for this session or date.',
          variant: 'destructive',
        });
        await refreshSummaryLink();
      } else {
        const fallback = typeof detail === 'string' ? detail : (detail?.message || err.message || 'Unknown error');
        toast({
          title: 'Launch failed',
          description: fallback,
          variant: 'destructive',
        });
      }
    } finally {
      setLaunchingSummary(false);
    }
  };

  const valuations = manifest?.valuations || [];
  const subcategories = manifest?.subcategories || [];
  const locations = manifest?.locations || [];
  const isMultiLocation = manifest?.session?.is_multi_location;
  const isMultiLocationParent = manifest?.session?.is_multi_location_parent;
  const jobId = manifest?.session?.job_id;
  const summaryLink = manifest?.session?.summary_link;
  const reports = manifest?.reports || [];
  const stockOnHandReport = reports.find(r => r.key === 'stock_on_hand');
  const estVsTillReport = reports.find(r => r.key === 'estimated_vs_till');
  const stockAuditReport = reports.find(r => r.key === 'stock_audit_report');
  const noValuations = stockOnHandReport?.requires_valuation && valuations.length === 0;
  const hasClosing = !!valuations.find(v => v.key === 'closing');
  const noSnapshotEst = estVsTillReport?.requires_snapshot && !hasClosing;
  const noSnapshotAudit = stockAuditReport?.requires_snapshot && !hasClosing;
  const sessionStatus = manifest?.session?.status;
  const notLockedValuation = sessionStatus !== 'locked';
  const noSnapshotValuation = !hasClosing || notLockedValuation;
  const noSnapshotCostMovement = !hasClosing;
  const execSummaryOutput = (futureManifest?.outputs || []).find(o => o.key === 'executive_summary');

  // Canonical session-aware report manifest (report_applicability.py) —
  // only entries applicable to THIS session's shape are present here,
  // grouped by family, each tagged 'available' or 'planned'. Families
  // with zero applicable entries are hidden entirely below.
  const catalog = manifest?.report_catalog || [];
  const byFamily = (fam) => catalog.filter(e => e.family === fam);
  const summaryCatalog = byFamily('summary');
  const coreCatalog = byFamily('core_stock_audit');
  const valuationCatalog = byFamily('valuation');
  const supportingCatalog = byFamily('supporting_schedules');
  const packCatalog = byFamily('full_report_pack');
  const hasCatalogKey = (list, key) => list.some(e => e.key === key);
  const filterableReportNames = catalog.filter(e => e.filterable).map(e => e.name);

  // Batch "Download All" (2026-09) — sequential + staggered so multiple
  // blob downloads don't all fire in the same tick; each handler already
  // toasts its own success/failure, so one bad report doesn't block the rest.
  const runBatch = async (batchKey, jobs) => {
    if (!jobs.length || batchRunning) return;
    setBatchRunning(batchKey);
    for (const job of jobs) {
      try { job(); } catch (_e) { /* individual handler already toasts */ }
      await new Promise((r) => setTimeout(r, 700));
    }
    setTimeout(() => setBatchRunning(null), 1200);
  };

  const summaryJobs = [
    hasCatalogKey(summaryCatalog, 'executive_summary') && !!execSummaryOutput && handleExecutiveSummary,
  ].filter(Boolean);
  const coreJobs = [
    !noSnapshotAudit && hasCatalogKey(coreCatalog, 'stock_audit_report') && handleStockAuditReport,
    hasCatalogKey(coreCatalog, 'stock_audit_analytics') && handleStockAuditAnalytics,
    !noSnapshotAudit && hasCatalogKey(coreCatalog, 'core_audit_history_reconciliation') && handleAuditHistoryReconciliation,
  ].filter(Boolean);
  const valuationJobs = [
    !noSnapshotValuation && hasCatalogKey(valuationCatalog, 'valuation_report') && handleCertificateOfValuation,
    !noSnapshotValuation && hasCatalogKey(valuationCatalog, 'detailed_stock_valuation') && handleDetailedStockValuation,
  ].filter(Boolean);
  const supportingJobs = [
    !noValuations && hasCatalogKey(supportingCatalog, 'stock_on_hand') && handleStockOnHand,
    !noSnapshotEst && hasCatalogKey(supportingCatalog, 'estimated_vs_till') && handleEstimatedVsTill,
    hasCatalogKey(supportingCatalog, 'deliveries_register') && handleDeliveriesRegister,
    hasCatalogKey(supportingCatalog, 'credits_register') && handleCreditsRegister,
    hasCatalogKey(supportingCatalog, 'transfers_register') && handleTransfersRegister,
    hasCatalogKey(supportingCatalog, 'wastage_allowances_detail') && handleWastageAdjustmentsDetail,
    !noSnapshotCostMovement && hasCatalogKey(supportingCatalog, 'cost_movement') && handleCostMovement,
    hasCatalogKey(supportingCatalog, 'sales_register') && handleSalesRegister,
  ].filter(Boolean);
  const allJobs = [...summaryJobs, ...coreJobs, ...valuationJobs, ...supportingJobs];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="reports-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 pr-6">
            <span className="flex items-center gap-2"><FileText className="h-4 w-4" />Report Centre</span>
            <DownloadAllButton
              label="Download All Reports"
              batchKey="all"
              batchRunning={batchRunning}
              jobCount={allJobs.length}
              onRun={() => runBatch('all', allJobs)}
              testId="report-centre-download-all-btn"
            />
          </DialogTitle>
          <DialogDescription>
            {manifest?.session?.site_name ? `${manifest.session.site_name} · ` : ''}
            {manifest?.session?.reference || ''} — pick a report below.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading session…
          </div>
        ) : (
          <div className="space-y-3">
            {/* Shared filters — apply to Stock On Hand, Estimated vs Till
                and Stock Audit Report downloads (unchanged behaviour). */}
            {(subcategories.length > 0 || (isMultiLocation && locations.length > 0)) && (
              <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
                {subcategories.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Sub-categories <span className="text-muted-foreground">(optional filter)</span></Label>
                    <p className="text-[10px] text-slate-500" data-testid="reports-dialog-subcat-scope-note">
                      Only scopes {filterableReportNames.length ? filterableReportNames.join(', ') : 'granular schedule reports'} — whole-audit reports (Executive Summary, Certificate of Valuation, etc.) always show full data.
                    </p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs justify-between w-full sm:w-64" data-testid="reports-dialog-subcat-dropdown-trigger">
                          {selectedSubcats.length > 0 ? `${selectedSubcats.length} selected` : 'All sub-categories'}
                          <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto" data-testid="reports-dialog-subcat-dropdown-content">
                        {subcategories.map(sub => (
                          <DropdownMenuCheckboxItem
                            key={sub}
                            checked={selectedSubcats.includes(sub)}
                            onCheckedChange={() => toggle(selectedSubcats, sub, setSelectedSubcats)}
                            onSelect={(e) => e.preventDefault()}
                            data-testid={`reports-dialog-subcat-${sub.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            {sub}
                          </DropdownMenuCheckboxItem>
                        ))}
                        {selectedSubcats.length > 0 && (
                          <>
                            <DropdownMenuSeparator />
                            <button
                              type="button"
                              onClick={() => setSelectedSubcats([])}
                              className="w-full text-left text-[11px] text-slate-500 hover:text-slate-700 px-2 py-1.5 flex items-center gap-1"
                              data-testid="reports-dialog-clear-subcats"
                            >
                              <X className="w-2.5 h-2.5" /> Clear selection
                            </button>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                {isMultiLocation && locations.length > 0 && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Locations <span className="text-muted-foreground">(leave empty for all)</span></Label>
                    <div className="flex flex-wrap gap-1">
                      {locations.map(loc => {
                        const active = selectedLocations.includes(loc.id);
                        return (
                          <button
                            key={loc.id}
                            type="button"
                            onClick={() => toggle(selectedLocations, loc.id, setSelectedLocations)}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                              active ? 'bg-emerald-700 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                            }`}
                            data-testid={`reports-dialog-location-${loc.id}`}
                          >
                            {loc.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {isMultiLocationParent && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="report-centre-parent-banner">
                This is a multi-location audit — open Reports from each location row in the results view below for per-location downloads.
              </p>
            )}

            <Accordion type="multiple" defaultValue={['summary']} className="w-full">
              {/* 1. Summary */}
              {summaryCatalog.length > 0 && (
              <AccordionItem value="summary">
                <AccordionTrigger data-testid="report-centre-section-summary">
                  <span className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-blue-600" />Summary</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  {/* Audit Summary Report — reverse-linked to the existing
                      Summary Wizard via site+date matching (Pass 1/2). No
                      duplicate workflow; PDF opens the existing report,
                      Create launches the existing Wizard against a
                      deterministically-matched Job. */}
                  {hasCatalogKey(summaryCatalog, 'audit_summary_report') && (
                  <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-audit-summary">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Audit Summary Report</span>
                      {summaryLink?.status === 'linked' && (
                        <Badge className="text-[9px] bg-green-100 text-green-800 border-green-300">Linked</Badge>
                      )}
                      {(summaryLink?.status === 'eligible_no_report' || (summaryLink?.status === 'none' && summaryLink?.launch_eligible)) && (
                        <Badge className="text-[9px] bg-amber-100 text-amber-800 border-amber-300">Not created yet</Badge>
                      )}
                    </div>
                    {!summaryLink ? (
                      <p className="text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Checking…</p>
                    ) : summaryLink.status === 'linked' ? (
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Stock # {summaryLink.report?.stock_number}</p>
                        <Button
                          size="sm"
                          onClick={() => goToSummaryReport(summaryLink.job_id, 'BEVERAGE', summaryLink.report?.id)}
                          data-testid="report-tile-audit-summary-pdf-btn"
                        >
                          <Eye className="h-3 w-3 mr-1" />View / PDF
                        </Button>
                      </div>
                    ) : (summaryLink.status === 'eligible_no_report' || summaryLink.status === 'none') ? (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">
                            {summaryLink.launch_eligible
                              ? (summaryLink.session_locked
                                  ? 'No Summary Report yet — launch one pre-filled from this locked Stocktake.'
                                  // 2026-02 correction: no exact-date Job exists yet (resolver
                                  // status 'none') — the draft still starts; Job attachment
                                  // becomes an explicit later step, never a precondition.
                                  : (summaryLink.status === 'none'
                                      ? 'No exact-date Job found yet — start a draft now from this calculated Stocktake; you can attach the Job later.'
                                      : 'No Summary Report yet — start a draft pre-filled from this calculated Stocktake.'))
                              : (summaryLink.status === 'eligible_no_report'
                                  ? 'No Summary Report yet for this audit date.'
                                  : (summaryLink.reason || 'No linked Summary Report found.'))}
                          </p>
                          {summaryLink.launch_eligible ? (
                            <Button
                              size="sm"
                              variant="default"
                              disabled={launchingSummary}
                              onClick={() => handleLaunchSummary(false)}
                              data-testid="report-tile-audit-summary-launch-btn"
                            >
                              {launchingSummary ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Link2 className="h-3 w-3 mr-1" />}
                              {summaryLink.session_locked ? 'Launch Linked Summary' : 'Start Summary Draft'}
                            </Button>
                          ) : summaryLink.status === 'eligible_no_report' ? (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => goToSummaryReport(summaryLink.job_id, summaryLink.scope)}
                              data-testid="report-tile-audit-summary-create-btn"
                            >
                              <Plus className="h-3 w-3 mr-1" />Create Summary Report
                            </Button>
                          ) : null}
                        </div>
                        {summaryLink.launch_eligible && summaryLink.resolver_status === 'suggested' && (
                          <p className="text-[11px] text-amber-700" data-testid="report-tile-audit-summary-nearest-hint">
                            A nearby Summary exists ({summaryLink.date_diff_days} day(s) away) — launching will ask you to confirm.
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-700" data-testid="report-tile-audit-summary-disabled-reason">{summaryLink.reason || 'No linked Summary Report found.'}</p>
                    )}
                  </div>
                  )}

                  {/* Executive Summary — hidden entirely when not applicable
                      to this session shape (see report_applicability.py) */}
                  {hasCatalogKey(summaryCatalog, 'executive_summary') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-executive-summary">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">Executive Summary</span>
                          <p className="text-xs text-muted-foreground">2-page management report — KPI trends, exceptions &amp; commentary.</p>
                        </div>
                        <Button
                          size="sm"
                          disabled={busyKey === 'executive_summary' || !execSummaryOutput}
                          onClick={handleExecutiveSummary}
                          data-testid="report-tile-executive-summary-btn"
                        >
                          {busyKey === 'executive_summary' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                          PDF
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Audit Findings & Actions tile removed from the visible
                      Report Centre for now — the Summary Wizard's General
                      Audit Comments + Actions Agreed are the operational
                      observations/actions process. Model/routes/PDF/XLSX
                      generators are untouched and still reachable directly. */}
                </AccordionContent>
              </AccordionItem>
              )}

              {/* 2. Core Stock Audit */}
              {coreCatalog.length > 0 && (
              <AccordionItem value="core">
                <AccordionTrigger data-testid="report-centre-section-core">
                  <span className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-indigo-600" />Core Stock Audit</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <DownloadAllButton
                    label="Download All"
                    batchKey="core"
                    batchRunning={batchRunning}
                    jobCount={coreJobs.length}
                    onRun={() => runBatch('core', coreJobs)}
                    testId="report-centre-section-core-download-all-btn"
                  />                  {hasCatalogKey(coreCatalog, 'stock_audit_report') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-stock-audit-report">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">Core Stock Audit Report</span>
                          <p className="text-xs text-muted-foreground">Result &amp; Performance, Stock Movement, Category Analysis, Key Variances and Allowances/Adjustments.</p>
                        </div>
                        <Button
                          size="sm"
                          disabled={busyKey === 'stock_audit_report_pdf' || noSnapshotAudit}
                          onClick={handleStockAuditReport}
                          data-testid="report-tile-stock-audit-report-btn"
                        >
                          {busyKey === 'stock_audit_report_pdf' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                          Download
                        </Button>
                      </div>
                      {noSnapshotAudit && <p className="text-[10px] text-amber-700 mt-1">Calculate the audit first — needs the closing-stock snapshot.</p>}
                    </div>
                  )}
                  {hasCatalogKey(coreCatalog, 'stock_audit_analytics') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-stock-audit-analytics">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">Stock Audit Analytics</span>
                          <p className="text-xs text-muted-foreground">Movement trail, KPIs &amp; variance analysis.</p>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <PackGenerateButton
                            packType="stock_audit_analytics"
                            job={packJobs.stock_audit_analytics}
                            onGenerate={handleStockAuditAnalytics}
                            onDownload={downloadPackVersion}
                            onRegenerate={() => startPackGeneration('stock_audit_analytics', true)}
                            downloading={downloadingVersionId === packJobs.stock_audit_analytics?.version_id}
                            label="PDF"
                            variant="default"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyKey === 'stock_audit_analytics_xlsx'}
                            onClick={handleStockAuditAnalyticsXlsx}
                            data-testid="report-tile-stock-audit-analytics-xlsx-btn"
                          >
                            {busyKey === 'stock_audit_analytics_xlsx' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileSpreadsheet className="h-3 w-3 mr-1" />}
                            Excel
                          </Button>
                        </div>
                      </div>
                      {packJobs.stock_audit_analytics?.status === 'failed' && (
                        <p className="text-[10px] text-red-700 mt-1" data-testid="stock-audit-analytics-failure-detail">
                          {packJobs.stock_audit_analytics.error_message || 'Unknown error'}
                        </p>
                      )}
                      {packJobs.stock_audit_analytics?.status === 'generating' && (
                        <p className="text-[10px] text-slate-600 mt-1 italic">
                          Building the PDF in the background — this stays running even if you close the dialog. Reopen to check status.
                        </p>
                      )}
                    </div>
                  )}
                  {hasCatalogKey(coreCatalog, 'core_audit_history_reconciliation') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-core-audit-history-reconciliation">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">Core Audit History &amp; Reconciliation</span>
                          <p className="text-xs text-muted-foreground">Audit history trends, Sales/PLU reconciliation, product exceptions and diagnostics.</p>
                        </div>
                        <ReportFormatButtons
                          baseKey="core_audit_history_reconciliation"
                          busyKey={busyKey}
                          disabled={noSnapshotAudit}
                          onPdf={handleAuditHistoryReconciliation}
                          onXlsx={handleAuditHistoryReconciliationXlsx}
                          testIdBase="report-tile-core-audit-history-reconciliation"
                        />
                      </div>
                      {noSnapshotAudit && <p className="text-[10px] text-amber-700 mt-1">Calculate the audit first — needs the closing-stock snapshot.</p>}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
              )}

              {/* 3. Valuation */}
              {valuationCatalog.length > 0 && (
              <AccordionItem value="valuation">
                <AccordionTrigger data-testid="report-centre-section-valuation">
                  <span className="flex items-center gap-2"><Scale className="h-4 w-4 text-emerald-600" />Valuation</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <DownloadAllButton
                    label="Download All"
                    batchKey="valuation"
                    batchRunning={batchRunning}
                    jobCount={valuationJobs.length}
                    onRun={() => runBatch('valuation', valuationJobs)}
                    testId="report-centre-section-valuation-download-all-btn"
                  />                  {hasCatalogKey(valuationCatalog, 'valuation_report') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-certificate-of-valuation">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">Certificate of Valuation</span>
                          <p className="text-xs text-muted-foreground">Client-facing Total Stock Value at Cost by category/sub-category.</p>
                        </div>
                        <Button
                          size="sm"
                          disabled={busyKey === 'valuation_report_pdf' || noSnapshotValuation}
                          onClick={handleCertificateOfValuation}
                          data-testid="report-tile-certificate-of-valuation-btn"
                        >
                          {busyKey === 'valuation_report_pdf' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                          Download
                        </Button>
                      </div>
                      {noSnapshotValuation && (
                        <p className="text-[10px] text-amber-700 mt-1">
                          {notLockedValuation
                            ? 'Lock (finalize) the audit first — Valuation reports only generate from a locked session.'
                            : 'Calculate the audit first — needs the closing-stock snapshot.'}
                        </p>
                      )}
                    </div>
                  )}
                  {hasCatalogKey(valuationCatalog, 'detailed_stock_valuation') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-detailed-stock-valuation">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">Detailed Stock Valuation</span>
                          <p className="text-xs text-muted-foreground">Full product-level breakdown with Unit Cost and valuation at cost.</p>
                        </div>
                        <ReportFormatButtons
                          baseKey="detailed_stock_valuation"
                          busyKey={busyKey}
                          disabled={noSnapshotValuation}
                          onPdf={handleDetailedStockValuation}
                          onXlsx={handleDetailedStockValuationXlsx}
                          testIdBase="report-tile-detailed-stock-valuation"
                        />
                      </div>
                      {noSnapshotValuation && (
                        <p className="text-[10px] text-amber-700 mt-1">
                          {notLockedValuation
                            ? 'Lock (finalize) the audit first — Valuation reports only generate from a locked session.'
                            : 'Calculate the audit first — needs the closing-stock snapshot.'}
                        </p>
                      )}
                    </div>
                  )}
                  {hasCatalogKey(valuationCatalog, 'combined_valuation') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-combined-valuation">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">Certificate & Detailed Valuation</span>
                          <p className="text-xs text-muted-foreground">One PDF — Certificate of Valuation followed by the Detailed Stock Valuation.</p>
                        </div>
                        <Button
                          size="sm"
                          disabled={busyKey === 'combined_valuation_pdf' || noSnapshotValuation}
                          onClick={handleCombinedValuation}
                          data-testid="report-tile-combined-valuation-btn"
                        >
                          {busyKey === 'combined_valuation_pdf' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                          Download
                        </Button>
                      </div>
                      {noSnapshotValuation && (
                        <p className="text-[10px] text-amber-700 mt-1">
                          {notLockedValuation
                            ? 'Lock (finalize) the audit first — Valuation reports only generate from a locked session.'
                            : 'Calculate the audit first — needs the closing-stock snapshot.'}
                        </p>
                      )}
                    </div>
                  )}

                </AccordionContent>
              </AccordionItem>
              )}

              {/* 4. Supporting Schedules */}
              {supportingCatalog.length > 0 && (
              <AccordionItem value="supporting">
                <AccordionTrigger data-testid="report-centre-section-supporting">
                  <span className="flex items-center gap-2"><Layers className="h-4 w-4 text-fuchsia-600" />Supporting Schedules</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <DownloadAllButton
                    label="Download All"
                    batchKey="supporting"
                    batchRunning={batchRunning}
                    jobCount={supportingJobs.length}
                    onRun={() => runBatch('supporting', supportingJobs)}
                    testId="report-centre-section-supporting-download-all-btn"
                  />                  {hasCatalogKey(supportingCatalog, 'stock_on_hand') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-stock-on-hand">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium">Stock On Hand</span>
                        <ReportFormatButtons
                          baseKey="stock_on_hand"
                          busyKey={busyKey}
                          disabled={noValuations}
                          onPdf={handleStockOnHand}
                          onXlsx={handleStockOnHandXlsx}
                          testIdBase="report-tile-stock-on-hand"
                        />
                      </div>
                      {noValuations ? (
                        <p className="text-[10px] text-amber-700">No valuation data available for this session yet.</p>
                      ) : (
                        <Select value={valuation} onValueChange={setValuation}>
                          <SelectTrigger className="h-8 text-xs w-44" data-testid="reports-dialog-valuation-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {valuations.map(v => (
                              <SelectItem key={v.key} value={v.key} data-testid={`reports-dialog-valuation-${v.key}`}>
                                {v.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                  {hasCatalogKey(supportingCatalog, 'estimated_vs_till') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-estimated-vs-till">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Estimated Sales vs Till Sales</span>
                        <ReportFormatButtons
                          baseKey="estimated_vs_till"
                          busyKey={busyKey}
                          disabled={noSnapshotEst}
                          onPdf={handleEstimatedVsTill}
                          onXlsx={handleEstimatedVsTillXlsx}
                          testIdBase="report-tile-estimated-vs-till"
                        />
                      </div>
                      {noSnapshotEst && <p className="text-[10px] text-amber-700 mt-1">Calculate the audit first — needs the closing-stock snapshot.</p>}
                    </div>
                  )}
                  {hasCatalogKey(supportingCatalog, 'deliveries_register') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-deliveries-register">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-slate-400" />Deliveries Register</span>
                          <p className="text-xs text-muted-foreground">Posted delivery lines for this session's site and period.</p>
                        </div>
                        <ReportFormatButtons
                          baseKey="deliveries_register"
                          busyKey={busyKey}
                          disabled={false}
                          onPdf={handleDeliveriesRegister}
                          onXlsx={handleDeliveriesRegisterXlsx}
                          testIdBase="report-tile-deliveries-register"
                        />
                      </div>
                    </div>
                  )}
                  {hasCatalogKey(supportingCatalog, 'credits_register') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-credits-register">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium flex items-center gap-1.5"><Undo2 className="h-3.5 w-3.5 text-slate-400" />Credits Register</span>
                          <p className="text-xs text-muted-foreground">Posted credit note lines for this session's site and period.</p>
                        </div>
                        <ReportFormatButtons
                          baseKey="credits_register"
                          busyKey={busyKey}
                          disabled={false}
                          onPdf={handleCreditsRegister}
                          onXlsx={handleCreditsRegisterXlsx}
                          testIdBase="report-tile-credits-register"
                        />
                      </div>
                    </div>
                  )}
                  {hasCatalogKey(supportingCatalog, 'transfers_register') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-transfers-register">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium flex items-center gap-1.5"><ArrowLeftRight className="h-3.5 w-3.5 text-slate-400" />Transfers Register</span>
                          <p className="text-xs text-muted-foreground">Posted internal transfers touching this location.</p>
                        </div>
                        <ReportFormatButtons
                          baseKey="transfers_register"
                          busyKey={busyKey}
                          disabled={false}
                          onPdf={handleTransfersRegister}
                          onXlsx={handleTransfersRegisterXlsx}
                          testIdBase="report-tile-transfers-register"
                        />
                      </div>
                    </div>
                  )}
                  {hasCatalogKey(supportingCatalog, 'wastage_allowances_detail') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-wastage-adjustments-detail">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium flex items-center gap-1.5"><Trash2 className="h-3.5 w-3.5 text-slate-400" />Wastage / Adjustments Detail</span>
                          <p className="text-xs text-muted-foreground">Itemised wastage, allowance and management entries.</p>
                        </div>
                        <ReportFormatButtons
                          baseKey="wastage_allowances_detail"
                          busyKey={busyKey}
                          disabled={false}
                          onPdf={handleWastageAdjustmentsDetail}
                          onXlsx={handleWastageAdjustmentsDetailXlsx}
                          testIdBase="report-tile-wastage-adjustments-detail"
                        />
                      </div>
                    </div>
                  )}
                  {hasCatalogKey(supportingCatalog, 'cost_movement') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-cost-movement">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-slate-400" />Cost Movement / Unit Cost Comparison</span>
                          <p className="text-xs text-muted-foreground">Current Unit Cost vs the site's previous locked audit only — not a supplier price log.</p>
                        </div>
                        <ReportFormatButtons
                          baseKey="cost_movement"
                          busyKey={busyKey}
                          disabled={noSnapshotCostMovement}
                          onPdf={handleCostMovement}
                          onXlsx={handleCostMovementXlsx}
                          testIdBase="report-tile-cost-movement"
                        />
                      </div>
                      {noSnapshotCostMovement && <p className="text-[10px] text-amber-700 mt-1">Calculate the audit first — needs the closing-stock snapshot.</p>}
                    </div>
                  )}
                  {hasCatalogKey(supportingCatalog, 'sales_register') && (
                    <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-sales-register">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5 text-slate-400" />Sales Register</span>
                          <p className="text-xs text-muted-foreground">Itemised sales entries reconciled to the Stock Audit Sales step.</p>
                        </div>
                        <ReportFormatButtons
                          baseKey="sales_register"
                          busyKey={busyKey}
                          disabled={false}
                          onPdf={handleSalesRegister}
                          onXlsx={handleSalesRegisterXlsx}
                          testIdBase="report-tile-sales-register"
                        />
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
              )}

              {/* 5. Full Report Pack */}
              {packCatalog.length > 0 && (
              <AccordionItem value="pack" className="border-b-0">
                <AccordionTrigger data-testid="report-centre-section-pack">
                  <span className="flex items-center gap-2"><Printer className="h-4 w-4 text-slate-600" />Full Report Pack</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-full-report-pack">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium">Full Report Pack</span>
                        <p className="text-xs text-muted-foreground">One merged PDF (Summary → Core → Valuation → Supporting) or one multi-sheet Excel workbook, from every report applicable/available right now.</p>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        <PackGenerateButton
                          packType="pdf"
                          job={packJobs.pdf}
                          onGenerate={() => startPackGeneration('pdf')}
                          onDownload={downloadPackVersion}
                          onRegenerate={() => startPackGeneration('pdf', true)}
                          downloading={downloadingVersionId === packJobs.pdf?.version_id}
                        />
                        <PackGenerateButton
                          packType="xlsx"
                          job={packJobs.xlsx}
                          onGenerate={() => startPackGeneration('xlsx')}
                          onDownload={downloadPackVersion}
                          onRegenerate={() => startPackGeneration('xlsx', true)}
                          downloading={downloadingVersionId === packJobs.xlsx?.version_id}
                        />
                      </div>
                    </div>
                    {(packJobs.pdf?.status === 'failed' || packJobs.xlsx?.status === 'failed') && (
                      <p className="text-[10px] text-red-700 mt-1" data-testid="report-pack-failure-detail">
                        {packJobs.pdf?.status === 'failed' && `PDF failed${packJobs.pdf.failed_component ? ` building '${packJobs.pdf.failed_component}'` : ''}: ${packJobs.pdf.error_message || 'Unknown error'}. `}
                        {packJobs.xlsx?.status === 'failed' && `Excel failed${packJobs.xlsx.failed_component ? ` building '${packJobs.xlsx.failed_component}'` : ''}: ${packJobs.xlsx.error_message || 'Unknown error'}.`}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[10px] text-muted-foreground" data-testid="report-pack-version-caption">
                        {packInfo?.pdf_pack_versions?.length ? `PDF: v${packInfo.pdf_pack_versions[0].version_number} generated by ${packInfo.pdf_pack_versions[0].uploaded_by_name || 'system'}` : 'No PDF pack generated yet.'}
                        {packInfo?.xlsx_pack_versions?.length ? ` · Excel: v${packInfo.xlsx_pack_versions[0].version_number}` : ''}
                      </p>
                      <Button size="sm" variant="outline" onClick={() => setSendPackOpen(true)} data-testid="report-pack-send-btn">
                        <Send className="h-3 w-3 mr-1" />Send Pack
                      </Button>
                    </div>
                    <p className="text-[10px] text-amber-700 mt-1 italic" data-testid="report-pack-test-mode-note">
                      Send Pack distributes the latest stored version — generate the pack first, then use Send Pack to email it.
                    </p>

                    {/* Pre-deployment defect sweep #2 — internal authorised
                        users can reopen/download previously stored immutable
                        Report Pack versions, reusing the existing internal
                        Report/ReportVersion repository download route +
                        permissions. Not exposed to the Client Portal. */}
                    {((packInfo?.pdf_pack_versions?.length || 0) + (packInfo?.xlsx_pack_versions?.length || 0)) > 0 && (
                      <div className="mt-2 border-t border-slate-100 pt-2">
                        <button
                          type="button"
                          onClick={() => setVersionHistoryOpen(o => !o)}
                          className="flex items-center gap-1 text-[10px] text-slate-600 hover:text-slate-900"
                          data-testid="report-pack-version-history-toggle"
                        >
                          <History className="h-3 w-3" />
                          Version History ({(packInfo.pdf_pack_versions?.length || 0) + (packInfo.xlsx_pack_versions?.length || 0)})
                          {versionHistoryOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </button>
                        {versionHistoryOpen && (
                          <div className="mt-1.5 space-y-1" data-testid="report-pack-version-history-list">
                            {[...(packInfo.pdf_pack_versions || []).map(v => ({ ...v, kind: 'PDF' })),
                              ...(packInfo.xlsx_pack_versions || []).map(v => ({ ...v, kind: 'Excel' }))]
                              .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at))
                              .map(v => (
                                <div
                                  key={`${v.kind}-${v.version_id}`}
                                  className="flex items-center justify-between text-[10px] bg-slate-50 rounded px-2 py-1"
                                  data-testid={`report-pack-version-row-${v.version_id}`}
                                >
                                  <span className="truncate">
                                    <span className="font-medium">{v.kind} v{v.version_number}</span>
                                    {' · '}{v.uploaded_by_name || 'system'}
                                    {v.uploaded_at ? ` · ${new Date(v.uploaded_at).toLocaleString('en-GB')}` : ''}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 text-[10px] shrink-0"
                                    disabled={downloadingVersionId === v.version_id}
                                    onClick={() => downloadPackVersion(v.version_id, `${v.kind === 'PDF' ? 'FullReportPack' : 'ExcelSchedulesPack'}_v${v.version_number}_${manifest?.session?.reference || sessionId}.${v.kind === 'PDF' ? 'pdf' : 'xlsx'}`)}
                                    data-testid={`report-pack-version-download-${v.version_id}`}
                                  >
                                    {downloadingVersionId === v.version_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                                    Download
                                  </Button>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
              )}

              {/* Internal-only diagnostic tool — never shown to Client Portal.
                  Tile stays visible even with no link so auditors know the
                  feature exists; only the action is disabled. */}
              {isInternalUser && (
              <AccordionItem value="reconciliation" className="border-b-0">
                <AccordionTrigger data-testid="report-centre-section-reconciliation" onClick={loadReconciliationLink}>
                  <span className="flex items-center gap-2"><Scale className="h-4 w-4 text-amber-600" />Report Reconciliation</span>
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                  <div className="rounded-md border border-slate-200 p-2.5" data-testid="report-tile-reconciliation">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium">Report Reconciliation</span>
                        <p className="text-xs text-muted-foreground">Internal testing tool — compares this Stocktake session against the existing Summary Audit Report benchmark.</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reconciliationLink?.status === 'none'}
                        onClick={() => setReconciliationOpen(true)}
                        data-testid="report-tile-reconciliation-btn"
                      >
                        Open
                      </Button>
                    </div>
                    {!reconciliationLink && (
                      <p className="text-[10px] text-slate-400 mt-1" data-testid="reconciliation-tile-loading">Checking link status…</p>
                    )}
                    {reconciliationLink?.status === 'none' && (
                      <p className="text-[10px] text-slate-500 mt-1" data-testid="reconciliation-tile-none">No linked Summary Report found.</p>
                    )}
                    {reconciliationLink?.status === 'suggested' && (
                      <p className="text-[10px] text-amber-700 mt-1" data-testid="reconciliation-tile-suggested">Suggested match — confirmation required.</p>
                    )}
                    {reconciliationLink?.status === 'ambiguous' && (
                      <p className="text-[10px] text-amber-700 mt-1" data-testid="reconciliation-tile-ambiguous">Multiple candidates — confirmation required.</p>
                    )}
                    {reconciliationLink?.status === 'linked' && (
                      <p className="text-[10px] text-emerald-700 mt-1" data-testid="reconciliation-tile-linked">
                        {reconciliationLink.match_type === 'job_id' ? 'Linked by Job.' : 'Exact Date/Scope Match.'}
                      </p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
              )}
            </Accordion>

            <div className="flex justify-between gap-2 pt-1">
              {onBack ? (
                <Button variant="ghost" size="sm" onClick={() => { onBack(); onOpenChange(false); }} data-testid="reports-dialog-back-btn">
                  <ArrowLeft className="h-3 w-3 mr-1" />Back
                </Button>
              ) : <span />}
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>

      <SendReportPackDialog
        open={sendPackOpen}
        onOpenChange={setSendPackOpen}
        sessionId={sessionId}
        suggestedRecipient={packInfo?.suggested_recipient}
        onSent={refreshPackInfo}
      />
      {isInternalUser && (
        <ReportReconciliationDialog
          open={reconciliationOpen}
          onOpenChange={setReconciliationOpen}
          sessionId={sessionId}
        />
      )}

      {/* D8 — nearest-date acknowledgement. A Summary within 60 days
          exists but isn't an exact match; launching a separate linked
          Summary requires explicit confirmation. */}
      <AlertDialog open={!!nearestAckDialog} onOpenChange={(v) => !v && setNearestAckDialog(null)}>
        <AlertDialogContent data-testid="launch-summary-nearest-ack-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Nearby Summary exists</AlertDialogTitle>
            <AlertDialogDescription>
              {nearestAckDialog?.message || 'A Summary near this date exists but is not an exact match.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="launch-summary-nearest-ack-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="launch-summary-nearest-ack-confirm"
              onClick={() => handleLaunchSummary(true)}
            >
              Proceed and create a separate Summary
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
